// Presentational half of /settings/api-keys (api-keys-settings.md, List
// archetype §4c): PageHeader (the sole chrome row) carrying the agent scope
// filter and the "Create key" action, the API-keys table with the shared
// ConfirmModal revoke confirm (§4a), the create Drawer, and the
// one-time-secret Modal.
// SPLIT from ApiKeysSettings.tsx so both halves meet the sweep file budgets;
// the container (queries, mutations, URL state) stays in ApiKeysSettings.tsx.

import { useEffect, useRef, useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../api/schema";
import { formatDateTime } from "../lib/format";
import {
  Badge,
  Button,
  Code,
  EmptyState,
  Page,
  PageHeader,
  Select,
  Text,
} from "../ui";
import {
  ApiKeyCreateDrawer,
  type ApiKeyCatalogTarget,
  type ApiKeyCreateInput,
} from "./ApiKeyCreateDrawer";
import { ApiKeySecretModal, type CreatedApiKey } from "./ApiKeySecretModal";
import { PaginatedTableSection } from "./shared/PaginatedTableSection";
import { ConfirmModal } from "./shared/ConfirmModal";
import { DateCell } from "./shared/DateCell";
import { NameWithId } from "./shared/NameWithId";

type Agent = components["schemas"]["AgentResponse"];
type Team = components["schemas"]["TeamResponse"];
type ApiKey = components["schemas"]["ApiKeyResponse"];
type ScopeCatalog = components["schemas"]["ApiKeyScopeCatalogResponse"];

function findAgentDisplayName(agents: Agent[], agentId: string): string | null {
  return agents.find((agent) => agent.id === agentId)?.display_name ?? null;
}

function findTeamDisplayName(teams: Team[], teamId: string): string | null {
  return teams.find((team) => team.id === teamId)?.display_name ?? null;
}

export function scopeLabel(
  teamId: string | null,
  agentId: string | null,
  teams: Team[],
  agents: Agent[],
): string {
  if (teamId == null && agentId == null) {
    return "Org-wide";
  }
  if (teamId != null) {
    const displayName = findTeamDisplayName(teams, teamId);
    return displayName
      ? `Team-locked: ${displayName}`
      : `Team-locked: ${teamId}`;
  }

  if (agentId == null) {
    return "Org-wide";
  }
  const displayName = findAgentDisplayName(agents, agentId);
  return displayName
    ? `Agent-locked: ${displayName}`
    : `Agent-locked: ${agentId}`;
}

// The revoke confirm's target — carries the row labels so the ConfirmModal
// can name the exact key without re-resolving from the row.
interface RevokeTarget {
  id: string;
  displayName: string;
  keyPrefix: string;
}

function apiKeyColumns(
  teams: Team[],
  agents: Agent[],
  revokingApiKeyId: string | undefined,
  onStartRevoke: (target: RevokeTarget) => void,
): Array<ColumnDef<ApiKey>> {
  return [
    {
      header: "Name",
      accessorKey: "display_name",
      cell: ({ row }) => (
        <NameWithId
          id={row.original.id}
          idLabel="API key ID"
          name={row.original.display_name}
          strong
        />
      ),
    },
    {
      header: "Scope",
      cell: ({ row }) => (
        <NameWithId
          id={row.original.team_id ?? row.original.agent_id}
          idLabel={row.original.team_id == null ? "Agent ID" : "Team ID"}
          name={scopeLabel(
            row.original.team_id,
            row.original.agent_id,
            teams,
            agents,
          )}
        />
      ),
    },
    {
      header: "Key prefix",
      cell: ({ row }) => <Code>{row.original.key_prefix}</Code>,
    },
    {
      header: "Scopes",
      cell: ({ row }) =>
        row.original.scopes.length > 0 ? (
          row.original.scopes.join(", ")
        ) : (
          <Text as="span" variant="muted">
            No scopes
          </Text>
        ),
    },
    {
      header: "Created",
      accessorKey: "created_at",
      cell: ({ row }) => <DateCell value={row.original.created_at} />,
    },
    {
      header: "Status",
      id: "status",
      accessorFn: (apiKey) =>
        apiKey.revoked_at == null ? "active" : "revoked",
      cell: ({ row }) =>
        row.original.revoked_at ? (
          <Badge>revoked {formatDateTime(row.original.revoked_at)}</Badge>
        ) : (
          <Badge tone="success">active</Badge>
        ),
    },
    {
      header: "Actions",
      cell: ({ row }) => {
        const apiKey = row.original;

        if (apiKey.revoked_at != null) {
          return (
            <Text as="span" variant="muted">
              —
            </Text>
          );
        }

        return (
          <Button
            disabled={revokingApiKeyId != null}
            size="sm"
            type="button"
            variant="danger"
            onClick={() => {
              onStartRevoke({
                id: apiKey.id,
                displayName: apiKey.display_name,
                keyPrefix: apiKey.key_prefix,
              });
            }}
          >
            Revoke
          </Button>
        );
      },
    },
  ];
}

export interface ApiKeysSettingsViewProps {
  agentIdFilter?: string;
  agents: Agent[];
  agentsErrorMessage?: string;
  apiKeys: ApiKey[];
  apiKeysErrorMessage?: string;
  canCreateOrgWide?: boolean;
  createdKey: CreatedApiKey | null;
  createErrorMessage?: string;
  hasMoreAgents: boolean;
  hasMoreApiKeys: boolean;
  hasMoreTeams: boolean;
  isAgentsFetchingNextPage: boolean;
  isAgentsPending: boolean;
  isApiKeysFetchingNextPage: boolean;
  isApiKeysPending: boolean;
  isCreateDrawerOpen: boolean;
  isCreatePending: boolean;
  isTeamsFetchingNextPage: boolean;
  isScopeCatalogPending: boolean;
  initialTeamId?: string;
  listStateKey: string;
  onAgentIdFilterChange: (value: string) => void;
  onCatalogTargetChange: (
    target: ApiKeyCatalogTarget | null | undefined,
  ) => void;
  onCloseCreateDrawer: () => void;
  onCreateSubmit: (input: ApiKeyCreateInput) => void;
  onDismissCreatedKey: () => void;
  onFetchNextAgents: () => void;
  onFetchNextApiKeys: () => void;
  onFetchNextTeams: () => void;
  onOpenCreateDrawer: () => void;
  onResetRevoke: () => void;
  onRevoke: (id: string) => Promise<void>;
  revokeErrorMessage?: string;
  revokingApiKeyId?: string;
  scopeCatalog?: ScopeCatalog;
  scopeCatalogErrorMessage?: string;
  teams: Team[];
  teamsErrorMessage?: string;
}

export function ApiKeysSettingsView({
  agentIdFilter,
  agents,
  agentsErrorMessage,
  apiKeys,
  apiKeysErrorMessage,
  canCreateOrgWide = true,
  createdKey,
  createErrorMessage,
  hasMoreAgents,
  hasMoreApiKeys,
  hasMoreTeams,
  isAgentsFetchingNextPage,
  isAgentsPending,
  isApiKeysFetchingNextPage,
  isApiKeysPending,
  isCreateDrawerOpen,
  isCreatePending,
  isTeamsFetchingNextPage,
  isScopeCatalogPending,
  initialTeamId,
  listStateKey,
  onAgentIdFilterChange,
  onCatalogTargetChange,
  onCloseCreateDrawer,
  onCreateSubmit,
  onDismissCreatedKey,
  onFetchNextAgents,
  onFetchNextApiKeys,
  onFetchNextTeams,
  onOpenCreateDrawer,
  onResetRevoke,
  onRevoke,
  revokeErrorMessage,
  revokingApiKeyId,
  scopeCatalog,
  scopeCatalogErrorMessage,
  teams,
  teamsErrorMessage,
}: ApiKeysSettingsViewProps) {
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);
  const createKeyButtonRef = useRef<HTMLButtonElement>(null);
  const wasSecretModalOpenRef = useRef(false);

  // Focus repair around the one-time-secret Modal. Create success closes the
  // drawer UNDER the opening Modal, which breaks the layer primitives' own
  // focus-restore chain in two ways:
  // 1. The closing Drawer's restore can land focus behind the open Modal —
  //    re-assert it onto the dialog panel (children's effects/cleanups run
  //    before this parent effect, so the Drawer restore has already fired).
  // 2. The Modal's captured restore target is the drawer's submit button,
  //    which unmounts with the drawer — on dismiss the restore is a no-op and
  //    focus falls to <body>; land it on the PageHeader "Create key" button.
  useEffect(() => {
    const isSecretModalOpen = createdKey != null;
    const wasSecretModalOpen = wasSecretModalOpenRef.current;
    wasSecretModalOpenRef.current = isSecretModalOpen;
    const active = document.activeElement;
    if (isSecretModalOpen) {
      if (
        !isCreateDrawerOpen &&
        active?.closest(".marker-modal__panel") == null
      ) {
        document.querySelector<HTMLElement>(".marker-modal__panel")?.focus();
      }
      return;
    }
    if (wasSecretModalOpen && (active == null || active === document.body)) {
      createKeyButtonRef.current?.focus();
    }
  }, [createdKey, isCreateDrawerOpen]);

  const closeRevoke = (): void => {
    onResetRevoke();
    setRevokeTarget(null);
  };

  const confirmRevoke = async (id: string): Promise<void> => {
    try {
      await onRevoke(id);
      setRevokeTarget(null);
    } catch {
      // The parent mutation owns error presentation; the confirm stays open
      // (revokeTarget untouched) so the modal can surface the error inline.
    }
  };

  const columns = apiKeyColumns(teams, agents, revokingApiKeyId, (target) => {
    onResetRevoke();
    setRevokeTarget(target);
  });

  const isRevoking =
    revokeTarget != null && revokingApiKeyId === revokeTarget.id;
  const revokeModalErrorMessage =
    revokeTarget != null && revokeErrorMessage ? revokeErrorMessage : null;

  // A deep-linked `?agent_id` beyond the loaded keyset pages still renders as
  // the selected option (scopeLabel's uuid fallback) — the filter Select never
  // claims "All API keys" while the list is filtered.
  const isFilterAgentLoaded =
    agentIdFilter == null ||
    findAgentDisplayName(agents, agentIdFilter) != null;

  return (
    <Page variant="workspace">
      {/* One chrome row: the agent scope is the screen's only filter, so it
          rides the header beside "Create key" instead of spending a second
          row above the table. Everything else the reader might narrow by —
          name, prefix, created, status — is already a sortable table column.
          §4b.3 keeps this row fixed: the table below is the scrollport, so
          "Create key" and the filter never scroll away from a long list. */}
      <PageHeader
        actions={
          <>
            <Select
              label="Agent"
              labelHidden
              size="sm"
              value={agentIdFilter ?? ""}
              onChange={(event) => {
                onAgentIdFilterChange(event.currentTarget.value);
              }}
            >
              <option value="">All API keys</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.display_name}
                </option>
              ))}
              {isFilterAgentLoaded ? null : (
                <option value={agentIdFilter}>
                  {scopeLabel(null, agentIdFilter, teams, agents)}
                </option>
              )}
            </Select>
            {hasMoreAgents ? (
              <Button
                disabled={isAgentsFetchingNextPage}
                size="sm"
                type="button"
                variant="secondary"
                onClick={onFetchNextAgents}
              >
                {isAgentsFetchingNextPage ? "Loading..." : "Load more agents"}
              </Button>
            ) : null}
            <Button
              ref={createKeyButtonRef}
              size="sm"
              type="button"
              onClick={onOpenCreateDrawer}
            >
              Create key
            </Button>
          </>
        }
      />
      <PaginatedTableSection
        ariaLabel="API keys"
        columns={columns}
        emptyState={
          <EmptyState
            action={
              <Button type="button" onClick={onOpenCreateDrawer}>
                Create key
              </Button>
            }
            title="No API keys"
          />
        }
        enableSorting
        errorMessage={apiKeysErrorMessage}
        getRowId={(apiKey) => apiKey.id}
        hasNextPage={hasMoreApiKeys}
        isFetchingNextPage={isApiKeysFetchingNextPage}
        isPending={isApiKeysPending}
        items={apiKeys}
        listStateKey={listStateKey}
        skeletonLabel="Loading API keys"
        skeletonRows={3}
        virtual={{ fill: true }}
        onLoadMore={onFetchNextApiKeys}
      />
      <ApiKeyCreateDrawer
        agents={agents}
        agentsErrorMessage={agentsErrorMessage}
        canCreateOrgWide={canCreateOrgWide}
        createErrorMessage={createErrorMessage}
        hasMoreAgents={hasMoreAgents}
        hasMoreTeams={hasMoreTeams}
        isAgentsFetchingNextPage={isAgentsFetchingNextPage}
        isAgentsPending={isAgentsPending}
        isCreatePending={isCreatePending}
        isTeamsFetchingNextPage={isTeamsFetchingNextPage}
        isOpen={isCreateDrawerOpen}
        isScopeCatalogPending={isScopeCatalogPending}
        initialTeamId={initialTeamId}
        onClose={onCloseCreateDrawer}
        onCatalogTargetChange={onCatalogTargetChange}
        onFetchNextAgents={onFetchNextAgents}
        onFetchNextTeams={onFetchNextTeams}
        onSubmit={onCreateSubmit}
        scopeCatalog={scopeCatalog}
        scopeCatalogErrorMessage={scopeCatalogErrorMessage}
        teams={teams}
        teamsErrorMessage={teamsErrorMessage}
      />
      <ApiKeySecretModal
        createdKey={createdKey}
        scopeLabel={
          createdKey
            ? scopeLabel(createdKey.team_id, createdKey.agent_id, teams, agents)
            : "Org-wide"
        }
        onDone={onDismissCreatedKey}
      />
      {revokeTarget ? (
        <ConfirmModal
          ariaLabel="Revoke API key?"
          confirmLabel="Revoke key"
          errorMessage={revokeModalErrorMessage}
          isPending={isRevoking}
          pendingLabel="Revoking..."
          sentence={`Revoke API key ${revokeTarget.displayName} (${revokeTarget.keyPrefix})? This disables the credential.`}
          onCancel={closeRevoke}
          onConfirm={() => {
            void confirmRevoke(revokeTarget.id);
          }}
        />
      ) : null}
    </Page>
  );
}
